import React, { useState, useEffect, useRef } from 'react'
import { apiGet } from '../../utils/api'
import { WhatsAppChat } from './WhatsAppArchiveView'

/* ─── Types ──────────────────────────────────────────────────────────── */

interface WAChatDetailPanelProps {
  chat: WhatsAppChat
}

interface WAMessage {
  id: number
  chat_id: number
  sender_name: string | null
  message_text: string | null
  message_date: string | null
  is_system: number
  has_media: number
  media_type: string | null
  media_filename: string | null
  media_original_name: string | null
}

interface ChatDetail extends WhatsAppChat {
  messages: WAMessage[]
  participants: string[]
  raw_text?: string
}

type TabId = 'conversation' | 'media' | 'info'

/* ─── Helper: Sender color generation ────────────────────────────────── */

function getSenderColor(name: string): string {
  const colors = ['#e17076', '#7bc862', '#e5c076', '#65aadd', '#ee7aae', '#6ec9cb', '#faa774', '#a695e7']
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length]
}

/* ─── Helper: Format file size ───────────────────────────────────────── */

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/* ─── Helper: Format date for display ────────────────────────────────── */

function formatMessageDate(dateStr: string | null): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
}

function formatDateDivider(dateStr: string | null): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

/* ─── Helper: Get media URL with auth token ──────────────────────────── */

function getMediaUrl(chatId: number, filename: string): string {
  const token = localStorage.getItem('auth_token') || ''
  return `/api/whatsapp-archive/${chatId}/media/${filename}?token=${encodeURIComponent(token)}`
}

/* ─── Main component ─────────────────────────────────────────────────── */

export const WAChatDetailPanel: React.FC<WAChatDetailPanelProps> = ({ chat }) => {
  const [detail, setDetail] = useState<ChatDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabId>('conversation')
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setDetail(null)

    apiGet<ChatDetail>(`/whatsapp-archive/${chat.id}`)
      .then((data) => {
        if (!cancelled) {
          setDetail(data)
          setLoading(false)
        }
      })
      .catch((err) => {
        console.error('Failed to fetch chat detail:', err)
        if (!cancelled) {
          setError((err as Error).message || 'Failed to load chat details')
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [chat.id])

  // Auto-scroll to bottom when messages load
  useEffect(() => {
    if (detail?.messages && activeTab === 'conversation') {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [detail?.messages, activeTab])

  const displayChat = detail || chat

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-secondary/20">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin w-8 h-8 text-brand-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-xs text-txt-secondary">Loading conversation...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-secondary/20 p-4">
        <div className="flex flex-col items-center gap-3 text-center">
          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-txt-secondary">{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* ─── Header ──────────────────────────────────────────────── */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-border bg-surface-primary">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-txt-primary dark:text-white truncate">
                {displayChat.group_name}
              </h2>
              <span className="text-[11px] font-mono bg-surface-tertiary text-txt-secondary px-2 py-1 rounded whitespace-nowrap">
                #{displayChat.chat_number}
              </span>
            </div>
            {displayChat.participant_count > 0 && (
              <p className="text-[11px] text-txt-secondary mt-1">
                {displayChat.participant_count} participant{displayChat.participant_count !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* ─── Tab buttons ─────────────────────────────────────── */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setActiveTab('conversation')}
              className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all ${
                activeTab === 'conversation'
                  ? 'bg-brand-600/10 text-brand-600'
                  : 'text-txt-secondary hover:bg-surface-secondary'
              }`}
            >
              Conversation
            </button>
            <button
              onClick={() => setActiveTab('media')}
              className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all ${
                activeTab === 'media'
                  ? 'bg-brand-600/10 text-brand-600'
                  : 'text-txt-secondary hover:bg-surface-secondary'
              }`}
            >
              Media
            </button>
            <button
              onClick={() => setActiveTab('info')}
              className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all ${
                activeTab === 'info'
                  ? 'bg-brand-600/10 text-brand-600'
                  : 'text-txt-secondary hover:bg-surface-secondary'
              }`}
            >
              Info
            </button>
          </div>
        </div>
      </div>

      {/* ─── Content area ────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'conversation' && detail && (
          <ConversationTab
            chat={displayChat}
            messages={detail.messages}
            messagesEndRef={messagesEndRef}
          />
        )}

        {activeTab === 'media' && detail && (
          <MediaGalleryTab
            chat={displayChat}
            messages={detail.messages}
          />
        )}

        {activeTab === 'info' && detail && (
          <InfoTab
            chat={displayChat}
            participants={detail.participants}
            messageCount={detail.message_count}
            mediaCount={detail.media_count}
          />
        )}
      </div>
    </div>
  )
}

/* ─── Conversation Tab ───────────────────────────────────────────────── */

const ConversationTab: React.FC<{
  chat: WhatsAppChat
  messages: WAMessage[]
  messagesEndRef: React.RefObject<HTMLDivElement>
}> = ({ chat, messages, messagesEndRef }) => {
  if (messages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-txt-secondary text-sm">
          <p>No messages available</p>
        </div>
      </div>
    )
  }

  // Group messages by date
  const messagesByDate: { [key: string]: WAMessage[] } = {}
  messages.forEach((msg) => {
    const dateKey = msg.message_date ? formatDateDivider(msg.message_date) : 'Unknown date'
    if (!messagesByDate[dateKey]) {
      messagesByDate[dateKey] = []
    }
    messagesByDate[dateKey].push(msg)
  })

  const sortedDates = Object.keys(messagesByDate).sort((a, b) => {
    const dateA = new Date(a)
    const dateB = new Date(b)
    return dateA.getTime() - dateB.getTime()
  })

  return (
    <div className="px-4 py-4 space-y-4 bg-[#e5ddd5] dark:bg-gray-900 min-h-full">
      {/* Chat start indicator */}
      <div className="flex justify-center">
        <span className="text-[10px] text-txt-secondary bg-[#d9fdd3]/60 dark:bg-gray-800 px-3 py-1 rounded-full">
          Chat #{chat.chat_number} • {chat.first_message_date ? new Date(chat.first_message_date).toLocaleDateString() : 'Unknown date'}
        </span>
      </div>

      {/* Messages grouped by date */}
      {sortedDates.map((dateKey) => (
        <div key={dateKey}>
          {/* Date divider */}
          <div className="flex justify-center mb-4">
            <span className="text-[10px] text-txt-secondary bg-[#d9fdd3]/60 dark:bg-gray-800 px-3 py-1 rounded-full">
              {dateKey}
            </span>
          </div>

          {/* Messages for this date */}
          <div className="space-y-2">
            {messagesByDate[dateKey].map((msg) => (
              <div key={msg.id}>
                {msg.is_system ? (
                  // System message
                  <div className="flex justify-center">
                    <span className="text-[11px] text-txt-secondary bg-[#d9fdd3]/60 dark:bg-gray-800 px-3 py-1 rounded-full text-center max-w-xs">
                      {msg.message_text}
                    </span>
                  </div>
                ) : (
                  // Regular message
                  <div className="flex justify-start mb-1">
                    <div className="max-w-[80%]">
                      {/* Sender name in colored text */}
                      {msg.sender_name && (
                        <div
                          className="text-[12px] font-semibold mb-0.5 pl-3"
                          style={{ color: getSenderColor(msg.sender_name) }}
                        >
                          {msg.sender_name}
                        </div>
                      )}

                      {/* Message bubble */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
                        {/* Message text */}
                        {msg.message_text && (
                          <p className="text-sm text-txt-primary dark:text-white break-words">
                            {msg.message_text}
                          </p>
                        )}

                        {/* Inline media */}
                        {msg.has_media ? (
                          <div className={msg.message_text ? 'mt-2' : ''}>
                            {/* Photo/image with file available */}
                            {(msg.media_type === 'photo' || msg.media_type === 'image') && msg.media_filename ? (
                              <img
                                src={getMediaUrl(chat.id, msg.media_filename)}
                                alt={msg.media_original_name || 'photo'}
                                className="rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                style={{ maxWidth: '180px', maxHeight: '140px' }}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                              />
                            ) : msg.media_type === 'video' && msg.media_filename ? (
                              <video
                                src={getMediaUrl(chat.id, msg.media_filename)}
                                controls
                                className="rounded-lg bg-black"
                                style={{ maxWidth: '180px', maxHeight: '140px' }}
                              />
                            ) : msg.media_type === 'audio' && msg.media_filename ? (
                              <audio
                                src={getMediaUrl(chat.id, msg.media_filename)}
                                controls
                                className="h-8"
                                style={{ maxWidth: '180px', width: '180px' }}
                              />
                            ) : msg.media_type === 'document' && msg.media_filename ? (
                              <a
                                href={getMediaUrl(chat.id, msg.media_filename)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: 'rgba(0,0,0,0.04)', minWidth: '200px', maxWidth: '280px', textDecoration: 'none' }}
                              >
                                <div className="flex items-center justify-center shrink-0" style={{
                                  width: '48px', height: '48px',
                                  backgroundColor: (msg.media_original_name || msg.media_filename || '').toLowerCase().endsWith('.pdf') ? '#E53935'
                                    : /\.(docx?)$/i.test(msg.media_original_name || msg.media_filename || '') ? '#1565C0'
                                    : /\.(xlsx?|csv)$/i.test(msg.media_original_name || msg.media_filename || '') ? '#2E7D32' : '#546E7A'
                                }}>
                                  {(msg.media_original_name || msg.media_filename || '').toLowerCase().endsWith('.pdf') ? (
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/></svg>
                                  ) : (
                                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0 py-2 pr-3">
                                  <p className="text-[12px] font-medium truncate" style={{ color: '#111b21' }}>{msg.media_original_name || msg.media_filename}</p>
                                  <p className="text-[10px] mt-0.5" style={{ color: '#667781' }}>
                                    {(msg.media_original_name || msg.media_filename || '').split('.').pop()?.toUpperCase() || 'FILE'}
                                  </p>
                                </div>
                              </a>
                            ) : (
                              /* Media omitted — show a styled placeholder */
                              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] ${
                                (msg.media_type === 'photo' || msg.media_type === 'image')
                                  ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                                  : msg.media_type === 'video'
                                  ? 'bg-purple-50 text-purple-600 border border-purple-100'
                                  : msg.media_type === 'audio'
                                  ? 'bg-indigo-50 text-indigo-600 border border-indigo-100'
                                  : 'bg-blue-50 text-blue-600 border border-blue-100'
                              }`}>
                                {(msg.media_type === 'photo' || msg.media_type === 'image') ? (
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                                  </svg>
                                ) : msg.media_type === 'video' ? (
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                                  </svg>
                                ) : msg.media_type === 'audio' ? (
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                  </svg>
                                )}
                                <span className="italic">
                                  {(msg.media_type === 'photo' || msg.media_type === 'image') ? 'Image' : msg.media_type === 'video' ? 'Video' : msg.media_type === 'audio' ? 'Audio' : 'Document'} not included in export
                                </span>
                              </div>
                            )}
                          </div>
                        ) : null}

                        {/* Timestamp */}
                        {msg.message_date && (
                          <div className="text-[10px] text-txt-secondary float-right ml-2 mt-1">
                            {formatMessageDate(msg.message_date)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </div>
  )
}

/* ─── Media Gallery Tab ──────────────────────────────────────────────── */

const MediaGalleryTab: React.FC<{
  chat: WhatsAppChat
  messages: WAMessage[]
}> = ({ chat }) => {
  const [filterType, setFilterType] = useState<'all' | 'photo' | 'video' | 'audio' | 'document'>('all')
  const [selectedMedia, setSelectedMedia] = useState<{ filename: string; type: string } | null>(null)
  const [allMedia, setAllMedia] = useState<{ filename: string; type: string; size?: number; originalName?: string }[]>([])
  const [mediaLoading, setMediaLoading] = useState(true)

  // Fetch media list from the API endpoint (reads actual files from disk)
  useEffect(() => {
    let cancelled = false
    setMediaLoading(true)
    apiGet(`/whatsapp-archive/${chat.id}/media`)
      .then((data: any) => {
        if (!cancelled && data?.media) {
          setAllMedia(data.media.map((m: any) => ({
            filename: m.filename,
            type: m.type || 'unknown',
            size: m.size,
            originalName: m.filename,
          })))
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setMediaLoading(false) })
    return () => { cancelled = true }
  }, [chat.id])

  // Filter by type
  const filteredMedia = filterType === 'all'
    ? allMedia
    : allMedia.filter((m) => m.type === filterType)

  // Get unique media (in case duplicates exist)
  const uniqueMedia = Array.from(
    new Map(filteredMedia.map((m) => [m.filename, m])).values()
  )

  if (mediaLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-txt-secondary text-sm">
          <p>Loading media...</p>
        </div>
      </div>
    )
  }

  if (allMedia.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-txt-secondary text-sm">
          <p>No media files in this chat</p>
        </div>
      </div>
    )
  }

  const photoCount = allMedia.filter((m) => m.type === 'photo').length
  const videoCount = allMedia.filter((m) => m.type === 'video').length
  const audioCount = allMedia.filter((m) => m.type === 'audio').length
  const docCount = allMedia.filter((m) => m.type === 'document').length

  return (
    <div className="h-full flex flex-col">
      {/* Filter tabs */}
      <div className="flex-shrink-0 border-b border-border bg-surface-primary px-4 py-3 flex items-center gap-2 overflow-x-auto">
        <button
          onClick={() => setFilterType('all')}
          className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all whitespace-nowrap ${
            filterType === 'all'
              ? 'bg-brand-600/10 text-brand-600'
              : 'text-txt-secondary hover:bg-surface-secondary'
          }`}
        >
          All ({allMedia.length})
        </button>
        {photoCount > 0 && (
          <button
            onClick={() => setFilterType('photo')}
            className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all whitespace-nowrap ${
              filterType === 'photo'
                ? 'bg-brand-600/10 text-brand-600'
                : 'text-txt-secondary hover:bg-surface-secondary'
            }`}
          >
            Photos ({photoCount})
          </button>
        )}
        {videoCount > 0 && (
          <button
            onClick={() => setFilterType('video')}
            className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all whitespace-nowrap ${
              filterType === 'video'
                ? 'bg-brand-600/10 text-brand-600'
                : 'text-txt-secondary hover:bg-surface-secondary'
            }`}
          >
            Videos ({videoCount})
          </button>
        )}
        {audioCount > 0 && (
          <button
            onClick={() => setFilterType('audio')}
            className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all whitespace-nowrap ${
              filterType === 'audio'
                ? 'bg-brand-600/10 text-brand-600'
                : 'text-txt-secondary hover:bg-surface-secondary'
            }`}
          >
            Audio ({audioCount})
          </button>
        )}
        {docCount > 0 && (
          <button
            onClick={() => setFilterType('document')}
            className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all whitespace-nowrap ${
              filterType === 'document'
                ? 'bg-brand-600/10 text-brand-600'
                : 'text-txt-secondary hover:bg-surface-secondary'
            }`}
          >
            Documents ({docCount})
          </button>
        )}
      </div>

      {/* Media grid — unified thumbnail view for all types */}
      <div className="flex-1 overflow-y-auto p-4">
        {uniqueMedia.length === 0 ? (
          <div className="text-center text-txt-secondary text-sm py-12">
            No {filterType !== 'all' ? filterType : 'media'} files
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '6px' }}>
            {uniqueMedia.map((media) => {
              const ext = media.filename.split('.').pop()?.toUpperCase() || ''
              const sizeLabel = media.size ? (media.size > 1048576 ? `${(media.size / 1048576).toFixed(1)} MB` : `${Math.round(media.size / 1024)} KB`) : ''

              return (
                <div
                  key={media.filename}
                  className="relative group cursor-pointer"
                  onClick={() => {
                    if (media.type === 'photo' || media.type === 'video') {
                      setSelectedMedia(media)
                    } else {
                      window.open(getMediaUrl(chat.id, media.filename), '_blank')
                    }
                  }}
                >
                  {/* Photo thumbnail */}
                  {media.type === 'photo' && (
                    <img
                      src={getMediaUrl(chat.id, media.filename)}
                      alt={media.originalName || 'photo'}
                      className="aspect-square object-cover rounded shadow-sm group-hover:shadow-md transition-shadow bg-gray-100"
                      style={{ width: '100%', height: '100%' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                  )}

                  {/* Video thumbnail with poster + play button */}
                  {media.type === 'video' && (
                    <>
                      <video
                        src={getMediaUrl(chat.id, media.filename)}
                        className="aspect-square object-cover rounded shadow-sm group-hover:shadow-md transition-shadow bg-gray-900"
                        style={{ width: '100%', height: '100%' }}
                        muted
                        preload="metadata"
                        onLoadedData={(e) => { (e.target as HTMLVideoElement).currentTime = 0.5 }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded group-hover:bg-black/40 transition-colors">
                        <div className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center shadow">
                          <svg className="w-3.5 h-3.5 text-gray-800 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </div>
                      </div>
                      <div className="absolute bottom-0.5 right-0.5 px-1 py-0.5 bg-black/70 rounded text-[7px] text-white font-medium">
                        {ext}
                      </div>
                    </>
                  )}

                  {/* Audio thumbnail card */}
                  {media.type === 'audio' && (
                    <div className="aspect-square rounded bg-gradient-to-br from-purple-500 to-indigo-600 shadow-sm group-hover:shadow-md transition-shadow flex flex-col items-center justify-center p-1 relative overflow-hidden">
                      <svg className="w-5 h-5 text-white/90 relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
                      </svg>
                      <p className="text-[7px] text-white/70 font-medium text-center truncate w-full px-0.5 relative z-10 mt-0.5">
                        {ext}
                      </p>
                    </div>
                  )}

                  {/* Document thumbnail card */}
                  {media.type === 'document' && (
                    <div className="aspect-square rounded bg-gradient-to-br from-blue-500 to-cyan-600 shadow-sm group-hover:shadow-md transition-shadow flex flex-col items-center justify-center p-1 relative overflow-hidden">
                      <svg className="w-5 h-5 text-white/90 relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                      </svg>
                      <p className="text-[7px] text-white/70 font-medium text-center truncate w-full px-0.5 relative z-10 mt-0.5">
                        {ext}
                      </p>
                    </div>
                  )}

                  {/* Unknown type fallback */}
                  {media.type !== 'photo' && media.type !== 'video' && media.type !== 'audio' && media.type !== 'document' && (
                    <div className="aspect-square rounded bg-gradient-to-br from-gray-400 to-gray-600 shadow-sm group-hover:shadow-md transition-shadow flex flex-col items-center justify-center p-1">
                      <svg className="w-5 h-5 text-white/90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                      </svg>
                      <p className="text-[7px] text-white/70 font-medium text-center truncate w-full px-0.5 mt-0.5">{ext}</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Lightbox overlay */}
      {selectedMedia && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <div
            className="max-w-4xl max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedMedia.type === 'photo' ? (
              <img
                src={getMediaUrl(chat.id, selectedMedia.filename)}
                alt="preview"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            ) : (
              <video
                src={getMediaUrl(chat.id, selectedMedia.filename)}
                controls
                className="max-w-full max-h-full rounded-lg"
              />
            )}
          </div>
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedMedia(null)}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

/* ─── Info Tab ───────────────────────────────────────────────────────── */

const InfoTab: React.FC<{
  chat: WhatsAppChat
  participants: string[]
  messageCount: number
  mediaCount: number
}> = ({ chat, participants, messageCount, mediaCount }) => {
  return (
    <div className="p-6 space-y-6 max-w-2xl">
      {/* Group Information */}
      <div>
        <h3 className="text-xs font-semibold text-txt-secondary uppercase tracking-wider mb-3">
          Group Information
        </h3>
        <div className="space-y-2 bg-surface-secondary rounded-lg p-4">
          <InfoItem label="Group Name" value={chat.group_name} />
          <InfoItem label="Chat Number" value={`#${chat.chat_number}`} />
          {chat.first_message_date && (
            <InfoItem
              label="Created"
              value={new Date(chat.first_message_date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            />
          )}
        </div>
      </div>

      {/* Participants */}
      <div>
        <h3 className="text-xs font-semibold text-txt-secondary uppercase tracking-wider mb-3">
          Participants ({participants.length})
        </h3>
        <div className="space-y-2">
          {participants.map((name, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-surface-secondary rounded-lg">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                style={{ backgroundColor: getSenderColor(name) }}
              >
                {name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || 'U'}
              </div>
              <div className="text-[12px] text-txt-primary dark:text-white truncate">{name}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics */}
      <div>
        <h3 className="text-xs font-semibold text-txt-secondary uppercase tracking-wider mb-3">
          Statistics
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Messages" value={String(messageCount)} />
          <StatCard label="Media Files" value={String(mediaCount)} />
          {chat.first_message_date && chat.last_message_date && (
            <>
              <StatCard
                label="First Message"
                value={new Date(chat.first_message_date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                })}
              />
              <StatCard
                label="Last Message"
                value={new Date(chat.last_message_date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                })}
              />
            </>
          )}
          {chat.zip_filesize > 0 && (
            <StatCard label="Archive Size" value={formatFileSize(chat.zip_filesize)} />
          )}
        </div>
      </div>
    </div>
  )
}

const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <dt className="text-[11px] text-txt-secondary">{label}</dt>
    <dd className="text-[12px] font-medium text-txt-primary dark:text-white text-right">{value}</dd>
  </div>
)

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-surface-secondary rounded-lg p-4 text-center">
    <div className="text-[11px] text-txt-secondary mb-1">{label}</div>
    <div className="text-lg font-semibold text-txt-primary dark:text-white">{value}</div>
  </div>
)
